import { useState } from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, ChevronDown, Loader2 } from "lucide-react";
import { PlanChangeSummaryList } from "@/components/ai/PlanChangeSummaryList";

/**
 * v1.3 Wave 2 — the hard stop before any data mutation. This is the ONLY
 * place in the entire UI where clicking a button actually triggers the
 * backend `/approve` call (via `onConfirm`, wired by the parent card to
 * `approvePlanAction`). Focus defaults to Cancel, per the design
 * guidelines' "safer default" accessibility rule.
 */
export const ActionPlanConfirmDialog = ({ open, onOpenChange, plan, onConfirm, loading }) => {
  const [showDetails, setShowDetails] = useState(false);
  const draftMessages = plan.draft_messages || [];
  const willNotify = draftMessages.length > 0;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="ai-action-plan-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Confirm execution</AlertDialogTitle>
          <AlertDialogDescription>
            Review exactly what will change before Your AI Office Manager applies it. This cannot be undone automatically.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="mt-3 rounded-lg border bg-[hsl(var(--secondary))] p-3">
          <PlanChangeSummaryList counts={plan.changes_preview} testIdPrefix="ai-action-plan-confirm-summary" />
        </div>

        {willNotify && (
          <div className="mt-3 flex gap-2 rounded-lg border border-[hsl(var(--ai-risk)/0.35)] bg-[hsl(var(--ai-risk)/0.10)] p-3 text-sm">
            <AlertTriangle className="h-4 w-4 shrink-0 text-[hsl(var(--ai-risk))]" />
            <span>{draftMessages.length} notification(s) will be queued to recipients once this executes.</span>
          </div>
        )}

        <Button
          type="button"
          variant="link"
          size="sm"
          className="mt-1 h-auto p-0 text-xs justify-start"
          onClick={() => setShowDetails((s) => !s)}
          data-testid="ai-action-plan-confirm-dialog-details-toggle"
        >
          <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-150 ${showDetails ? "rotate-180" : ""}`} />
          {showDetails ? "Hide details" : "Show details"}
        </Button>
        {showDetails && (
          <ul className="mt-2 max-h-40 space-y-1 overflow-y-auto text-xs text-muted-foreground" data-testid="ai-action-plan-confirm-dialog-details-list">
            {(plan.recommendations || []).map((rec, idx) => (
              <li key={rec.appointment_id || idx}>
                {rec.student_name}: {rec.reasoning || "See plan for details"}
              </li>
            ))}
            {(plan.recommendations || []).length === 0 && <li>No per-lesson changes for this plan.</li>}
          </ul>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel data-testid="ai-action-plan-confirm-dialog-cancel-button">Go back</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              onConfirm();
            }}
            disabled={loading}
            className="bg-[hsl(var(--role-accent))] hover:opacity-95"
            data-testid="ai-action-plan-confirm-dialog-confirm-button"
          >
            {loading ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Executing&hellip;
              </>
            ) : willNotify ? (
              "Execute & send messages"
            ) : (
              "Execute plan"
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
